import React, { useState } from 'react';
import axios from 'axios';
import './UploadTab.css';

// Используем прокси через nginx или прямой URL
// Важно: nginx проксирует /audio-api/ на audio-backend:8002/
// Поэтому используем /audio-api (без слэша), nginx автоматически добавит слэш
const AUDIO_BACKEND_URL = process.env.REACT_APP_AUDIO_BACKEND_URL || '/audio-api';

function UploadTab() {
  const [file, setFile] = useState(null);
  const [filename, setFilename] = useState('');
  const [languageCode, setLanguageCode] = useState('ru-RU'); // По умолчанию русский
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);

  const getFileExtension = (filename) => {
    const name = filename.toLowerCase();
    if (name.endsWith('.wav')) return '.wav';
    if (name.endsWith('.ogg')) return '.ogg';
    if (name.endsWith('.opus')) return '.opus';
    if (name.endsWith('.mp3')) return '.mp3';
    return null;
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      // Проверяем размер файла (максимум 200MB)
      const maxSize = 200 * 1024 * 1024; // 200MB
      if (selectedFile.size > maxSize) {
        setError(`Файл слишком большой. Максимальный размер: 200MB. Текущий размер: ${formatFileSize(selectedFile.size)}`);
        setFile(null);
        return;
      }
      
      // Проверяем формат файла (WAV, OGG, OPUS, MP3)
      const fileExt = getFileExtension(selectedFile.name);
      if (!fileExt) {
        setError('Поддерживаются только файлы форматов WAV, OGG/OPUS и MP3');
        setFile(null);
        return;
      }
      
      setFile(selectedFile);
      
      // Автоматически определяем и добавляем расширение к имени файла
      if (filename) {
        // Если имя уже указано, проверяем есть ли расширение
        const currentExt = getFileExtension(filename);
        if (!currentExt) {
          // Если расширения нет, добавляем расширение загруженного файла
          setFilename(filename + fileExt);
        } else if (currentExt !== fileExt) {
          // Если расширение отличается, заменяем его
          const nameWithoutExt = filename.substring(0, filename.lastIndexOf(currentExt));
          setFilename(nameWithoutExt + fileExt);
        }
      } else {
        // Если имя не указано, используем имя файла
        setFilename(selectedFile.name);
      }
      
      setError(null);
      setSuccess(null);
    }
  };

  const handleFilenameChange = (e) => {
    // Просто сохраняем то, что вводит пользователь, без автоматического добавления расширения
    // Расширение будет добавлено автоматически при выборе файла или при отправке формы
    setFilename(e.target.value);
    setError(null);
    setSuccess(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!file) {
      setError('Пожалуйста, выберите файл');
      return;
    }

    if (!filename || filename.trim() === '') {
      setError('Пожалуйста, укажите имя файла');
      return;
    }

    // Автоматически определяем расширение загруженного файла
    const fileExt = getFileExtension(file.name);
    if (!fileExt) {
      setError('Неподдерживаемый формат файла');
      return;
    }

    // Проверяем, что имя файла заканчивается правильным расширением
    const filenameLower = filename.toLowerCase();
    const supportedExts = ['.wav', '.ogg', '.opus', '.mp3'];
    const hasExt = supportedExts.some(ext => filenameLower.endsWith(ext));
    
    let finalFilename = filename;
    if (!hasExt) {
      // Если расширения нет, добавляем расширение загруженного файла
      finalFilename = filename + fileExt;
    } else {
      // Если расширение есть, но не совпадает с загруженным файлом, заменяем его
      const currentExt = supportedExts.find(ext => filenameLower.endsWith(ext));
      if (currentExt && currentExt !== fileExt) {
        const nameWithoutExt = filename.substring(0, filename.lastIndexOf(currentExt));
        finalFilename = nameWithoutExt + fileExt;
      }
    }

    setUploading(true);
    setError(null);
    setSuccess(null);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('filename', finalFilename);
      formData.append('language_code', languageCode);

      const response = await axios.post(
        `${AUDIO_BACKEND_URL}/upload`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setUploadProgress(percentCompleted);
            }
          },
          // Увеличиваем таймаут для больших файлов
          timeout: 300000, // 5 минут
        }
      );

      setSuccess({
        message: 'Файл успешно загружен и отправлен на распознавание!',
        data: response.data,
      });
      
      // Очищаем форму
      setFile(null);
      setFilename('');
      setLanguageCode('ru-RU'); // Сбрасываем на русский по умолчанию
      setUploadProgress(0);
      
      // Очищаем input файла
      const fileInput = document.getElementById('file-input');
      if (fileInput) {
        fileInput.value = '';
      }
    } catch (err) {
      let errorMessage = 'Ошибка при загрузке файла';
      
      if (err.response) {
        // Сервер ответил с кодом ошибки
        const status = err.response.status;
        const data = err.response.data;
        
        if (status === 400) {
          errorMessage = `Ошибка валидации: ${data?.detail || data?.message || 'Некорректные данные'}`;
        } else if (status === 413) {
          errorMessage = 'Файл слишком большой. Максимальный размер: 200MB';
        } else if (status === 500) {
          errorMessage = `Ошибка сервера: ${data?.detail || data?.message || 'Внутренняя ошибка сервера'}`;
        } else if (status === 503) {
          errorMessage = 'Сервис временно недоступен. Попробуйте позже.';
        } else {
          errorMessage = `Ошибка ${status}: ${data?.detail || data?.message || 'Неизвестная ошибка'}`;
        }
      } else if (err.request) {
        // Запрос был отправлен, но ответа не получено
        if (err.code === 'ECONNABORTED') {
          errorMessage = 'Превышено время ожидания. Файл слишком большой или сервер не отвечает.';
        } else {
          errorMessage = 'Не удалось подключиться к серверу. Проверьте подключение к интернету.';
        }
      } else {
        // Ошибка при настройке запроса
        errorMessage = `Ошибка: ${err.message}`;
      }
      
      setError(errorMessage);
      setUploadProgress(0);
      console.error('Upload error:', err);
    } finally {
      setUploading(false);
    }
  };

  const handleReset = () => {
    setFile(null);
    setFilename('');
    setLanguageCode('ru-RU');
    setError(null);
    setSuccess(null);
    setUploadProgress(0);
    const fileInput = document.getElementById('file-input');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  return (
    <div className="upload-tab">
      <div className="tab-header">
        <h2>Загрузка аудиофайла</h2>
      </div>

      <div className="info-box">
        <p>🎤 Загрузите аудиофайл для распознавания речи через Yandex Speech-to-Text.</p>
        <p>📁 Поддерживаемые форматы: WAV (16-bit PCM), OGG/OPUS, MP3</p>
        <p>📝 После загрузки файл будет отправлен на обработку, и вы сможете отслеживать процесс в разделе "Операции распознавания".</p>
      </div>

      {error && <div className="error-message">{error}</div>}

      {success && (
        <div className="success-message">
          <div className="success-header">
            <span className="success-icon">✅</span>
            <strong>{success.message}</strong>
          </div>
          {success.data && (
            <div className="success-details">
              <p><strong>Имя файла:</strong> {success.data.filename}</p>
              <p><strong>Operation ID:</strong> <code>{success.data.operation_id}</code></p>
              {success.data.s3_url && (
                <p>
                  <strong>S3 URL:</strong>{' '}
                  <a href={success.data.s3_url} target="_blank" rel="noopener noreferrer" className="s3-link">
                    {success.data.s3_url}
                  </a>
                </p>
              )}
            </div>
          )}
        </div>
      )}

      <form onSubmit={handleSubmit} className="upload-form">
        <div className="form-group">
          <label htmlFor="file-input">
            Выберите аудиофайл <span className="required">*</span>
          </label>
          <div className="file-input-wrapper">
            <input
              type="file"
              id="file-input"
              accept=".wav,.ogg,.opus,.mp3,audio/wav,audio/ogg,audio/opus,audio/mpeg"
              onChange={handleFileChange}
              disabled={uploading}
              className="file-input"
            />
            {file && (
              <div className="file-info">
                <span className="file-name">📄 {file.name}</span>
                <span className="file-size">({formatFileSize(file.size)})</span>
              </div>
            )}
          </div>
          {!file && (
            <small className="form-hint">
              Поддерживаются файлы форматов: WAV, OGG/OPUS, MP3
            </small>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="filename-input">
            Имя файла <span className="required">*</span>
          </label>
          <input
            type="text"
            id="filename-input"
            value={filename}
            onChange={handleFilenameChange}
            placeholder="например: interview_2026_01_06.mp3"
            disabled={uploading}
            className="text-input"
          />
          <small className="form-hint">
            Имя файла будет использовано для сохранения в S3 и идентификации результата распознавания.
            Расширение файла будет автоматически определено и добавлено к указанному имени.
          </small>
        </div>

        <div className="form-group">
          <label htmlFor="language-select">
            Язык распознавания <span className="required">*</span>
          </label>
          <select
            id="language-select"
            value={languageCode}
            onChange={(e) => setLanguageCode(e.target.value)}
            disabled={uploading}
            className="select-input"
          >
            <option value="ru-RU">Русский (ru-RU)</option>
            <option value="en-US">Английский (en-US)</option>
            <option value="en-GB">Английский (en-GB)</option>
            <option value="tr-TR">Турецкий (tr-TR)</option>
            <option value="uk-UA">Украинский (uk-UA)</option>
            <option value="kk-KZ">Казахский (kk-KZ)</option>
          </select>
          <small className="form-hint">
            Выберите язык, на котором говорят в аудиофайле. Это важно для корректного распознавания речи.
          </small>
        </div>

        {uploading && (
          <div className="upload-progress">
            <div className="progress-bar">
              <div
                className="progress-fill"
                style={{ width: `${uploadProgress}%` }}
              ></div>
            </div>
            <span className="progress-text">Загрузка: {uploadProgress}%</span>
          </div>
        )}

        <div className="form-actions">
          <button
            type="submit"
            className="btn btn-primary"
            disabled={uploading || !file || !filename.trim()}
          >
            {uploading ? '⏳ Загрузка...' : '📤 Загрузить и отправить на распознавание'}
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleReset}
            disabled={uploading}
          >
            Очистить
          </button>
        </div>
      </form>
    </div>
  );
}

export default UploadTab;

